import React from "react";
import {
  AbsoluteFill,
  Audio,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import {
  CheckCircle2,
  Zap,
  ShieldCheck,
  Globe,
  Lock,
  ArrowRight,
  Search,
} from "lucide-react";

export const KyuStoreWebLuminousBento: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Springs helper with custom damping
  const sp = (delay: number, damping = 14, stiffness = 130) =>
    spring({
      frame: frame - delay,
      fps,
      config: { damping, stiffness, mass: 1.0 },
    });

  // Color Tokens — 2026 Luminous Tech Bento
  const bgObsidian = "#080911";
  const cardSurface = "rgba(18, 20, 32, 0.75)";
  const cardBorder = "rgba(139, 92, 246, 0.25)";
  const cyanAccent = "#06B6D4";
  const purpleAccent = "#8B5CF6";
  const emeraldAccent = "#10B981";
  const textWhite = "#F8FAFC";
  const textMuted = "#94A3B8";

  // Ambient Breathing & Glow Motion
  const ambientPulse = Math.sin(frame / 25) * 6;
  const glowScale = interpolate(Math.sin(frame / 30), [-1, 1], [0.95, 1.05]);

  // Timed Subtitles (31s = ~930 frames)
  const SUBTITLES = [
    { from: 0, to: 120, text: "Masih jaman top-up game nunggu admin bales berjam-jam?", highlight: "nunggu admin" },
    { from: 120, to: 270, text: "Udah nunggu lama... salah ketik ID pula. Nih, web baru Kyu Store!", highlight: "web baru Kyu Store" },
    { from: 270, to: 480, text: "Tampilan baru jauh lebih kenceng, cukup ketik User ID langsung kedeteksi!", highlight: "langsung kedeteksi" },
    { from: 480, to: 690, text: "Bayar tinggal scan QRIS, 0.8 detik saldo langsung mendarat otomatis!", highlight: "0.8 detik" },
    { from: 690, to: 935, text: "Gak pake ribet login, gak pake password. Cobain di kyustore.com!", highlight: "kyustore.com" },
  ];

  const currentSub = SUBTITLES.find((s) => frame >= s.from && frame < s.to);

  // Header Animation
  const headSpring = sp(5, 16, 120);

  return (
    <AbsoluteFill
      style={{
        backgroundColor: bgObsidian,
        color: textWhite,
        fontFamily: "'Rajdhani', -apple-system, BlinkMacSystemFont, sans-serif",
        overflow: "hidden",
      }}
    >
      {/* 1. Ambient Dynamic Light Mesh */}
      <div
        style={{
          position: "absolute",
          top: -200,
          left: "50%",
          transform: `translateX(-50%) scale(${glowScale})`,
          width: 900,
          height: 900,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(139, 92, 246, 0.22) 0%, rgba(6, 182, 212, 0.12) 40%, transparent 70%)",
          filter: "blur(90px)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: -150,
          right: -100,
          width: 700,
          height: 700,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(16, 185, 129, 0.15) 0%, transparent 65%)",
          filter: "blur(80px)",
          pointerEvents: "none",
        }}
      />

      {/* Blueprint Grid Lines */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
          opacity: 0.6,
        }}
      />

      {/* 2. Top Header Zone (Safe Zone Y: 240 - 360 px) */}
      <div
        style={{
          position: "absolute",
          top: 260,
          left: 0,
          right: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          opacity: interpolate(headSpring, [0, 1], [0, 1]),
          transform: `translateY(${interpolate(headSpring, [0, 1], [-30, 0])}px)`,
        }}
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            padding: "8px 22px",
            borderRadius: 999,
            background: "rgba(139, 92, 246, 0.15)",
            border: "1px solid rgba(139, 92, 246, 0.4)",
            boxShadow: "0 0 25px rgba(139, 92, 246, 0.25)",
            backdropFilter: "blur(12px)",
          }}
        >
          <div
            style={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              backgroundColor: emeraldAccent,
              boxShadow: `0 0 12px ${emeraldAccent}`,
            }}
          />
          <span
            style={{
              fontSize: 20,
              fontWeight: 700,
              letterSpacing: "0.1em",
              color: textWhite,
              textTransform: "uppercase",
            }}
          >
            KYUSTORE.COM • WEB REVOLUTION 2.0
          </span>
        </div>

        <h1
          style={{
            marginTop: 18,
            fontSize: 54,
            fontWeight: 800,
            letterSpacing: "-0.01em",
            textAlign: "center",
            textTransform: "uppercase",
            lineHeight: 1.1,
            background: "linear-gradient(180deg, #FFFFFF 30%, #94A3B8 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            margin: "16px 40px 0",
          }}
        >
          Top-Up Sat-Set, Tanpa Ribet
        </h1>
      </div>

      {/* 3. Hero Bento Grid Stage (Y: 420 - 1440 px) */}
      <div
        style={{
          position: "absolute",
          top: 450,
          left: 70,
          right: 70,
          display: "flex",
          flexDirection: "column",
          gap: 24,
        }}
      >
        {/* Bento Card 1: Interactive Browser Bar & Auto Cek ID */}
        <div
          style={{
            background: cardSurface,
            border: `1.5px solid ${cardBorder}`,
            borderRadius: 28,
            padding: "26px 30px",
            boxShadow: "0 20px 50px rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(20px)",
            position: "relative",
            overflow: "hidden",
            transform: `translateY(${ambientPulse}px)`,
          }}
        >
          {/* Subtle Specular Sheen Sweep */}
          <div
            style={{
              position: "absolute",
              top: 0,
              left: "-100%",
              width: "200%",
              height: "100%",
              background:
                "linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.06) 45%, rgba(255,255,255,0.12) 50%, transparent 55%)",
              transform: `translateX(${(frame % 150) * 1.5}%)`,
              pointerEvents: "none",
            }}
          />

          {/* Browser Address Bar */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "10px 18px",
              background: "rgba(0, 0, 0, 0.4)",
              border: "1px solid rgba(255, 255, 255, 0.08)",
              borderRadius: 16,
              marginBottom: 20,
            }}
          >
            <Lock size={18} color={emeraldAccent} />
            <span style={{ fontSize: 22, color: cyanAccent, fontWeight: 700, letterSpacing: "0.02em" }}>
              https://kyustore.com
            </span>
            <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#EF4444" }} />
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#F59E0B" }} />
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#10B981" }} />
            </div>
          </div>

          {/* Verification Box Simulation */}
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 24, fontWeight: 700, color: textMuted }}>FITUR CEK USER ID REAL-TIME</span>
              <span
                style={{
                  fontSize: 16,
                  padding: "4px 12px",
                  borderRadius: 8,
                  background: "rgba(16, 185, 129, 0.15)",
                  color: emeraldAccent,
                  fontWeight: 700,
                }}
              >
                AUTOMATIC
              </span>
            </div>

            {/* Input Simulation */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "16px 20px",
                background: "rgba(255, 255, 255, 0.04)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                borderRadius: 18,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <Search size={22} color={cyanAccent} />
                <span style={{ fontSize: 28, fontWeight: 800, color: textWhite, letterSpacing: "0.05em" }}>
                  ID: 88492019 (2481)
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "6px 14px",
                  background: "rgba(16, 185, 129, 0.2)",
                  border: `1px solid ${emeraldAccent}`,
                  borderRadius: 12,
                }}
              >
                <CheckCircle2 size={18} color={emeraldAccent} />
                <span style={{ fontSize: 18, fontWeight: 800, color: emeraldAccent }}>
                  ZURA (VALID)
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Bento Row 2: Split Cards */}
        <div style={{ display: "flex", gap: 20 }}>
          {/* Card Left: 0.8s QRIS Speed Metric */}
          <div
            style={{
              flex: 1,
              background: cardSurface,
              border: `1.5px solid ${cardBorder}`,
              borderRadius: 28,
              padding: "26px 24px",
              boxShadow: "0 20px 40px rgba(0, 0, 0, 0.4)",
              backdropFilter: "blur(20px)",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              height: 240,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 14,
                  background: "rgba(6, 182, 212, 0.15)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "1px solid rgba(6, 182, 212, 0.3)",
                }}
              >
                <Zap size={26} color={cyanAccent} />
              </div>
              <span
                style={{
                  fontSize: 14,
                  fontWeight: 800,
                  color: cyanAccent,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                }}
              >
                SPEED ENGINE
              </span>
            </div>

            <div>
              <div style={{ fontSize: 62, fontWeight: 900, lineHeight: 1, color: textWhite, letterSpacing: "-0.03em" }}>
                0.8s
              </div>
              <div style={{ fontSize: 20, fontWeight: 700, color: textMuted, marginTop: 4 }}>
                QRIS Flash Settlement
              </div>
            </div>
          </div>

          {/* Card Right: 100% Otomatis / No Password */}
          <div
            style={{
              flex: 1,
              background: cardSurface,
              border: `1.5px solid ${cardBorder}`,
              borderRadius: 28,
              padding: "26px 24px",
              boxShadow: "0 20px 40px rgba(0, 0, 0, 0.4)",
              backdropFilter: "blur(20px)",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              height: 240,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 14,
                  background: "rgba(139, 92, 246, 0.15)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "1px solid rgba(139, 92, 246, 0.3)",
                }}
              >
                <ShieldCheck size={26} color={purpleAccent} />
              </div>
              <span
                style={{
                  fontSize: 14,
                  fontWeight: 800,
                  color: purpleAccent,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                }}
              >
                100% PRIVATE
              </span>
            </div>

            <div>
              <div style={{ fontSize: 32, fontWeight: 900, lineHeight: 1.1, color: textWhite }}>
                TANPA LOGIN & PASSWORD
              </div>
              <div style={{ fontSize: 18, fontWeight: 700, color: textMuted, marginTop: 6 }}>
                Cukup Masukkan ID Game
              </div>
            </div>
          </div>
        </div>

        {/* Bento Row 3: Call to Action Bar */}
        <div
          style={{
            background: "linear-gradient(90deg, rgba(139, 92, 246, 0.3) 0%, rgba(6, 182, 212, 0.3) 100%)",
            border: "1.5px solid rgba(255, 255, 255, 0.2)",
            borderRadius: 24,
            padding: "20px 28px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            boxShadow: "0 10px 30px rgba(139, 92, 246, 0.3)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <Globe size={28} color={textWhite} />
            <div>
              <div style={{ fontSize: 28, fontWeight: 900, color: textWhite, letterSpacing: "0.02em" }}>
                KUNJUNGI KYUSTORE.COM
              </div>
              <div style={{ fontSize: 18, fontWeight: 600, color: "rgba(255, 255, 255, 0.8)" }}>
                Server Otomatis 24 Jam Non-Stop
              </div>
            </div>
          </div>
          <div
            style={{
              width: 50,
              height: 50,
              borderRadius: "50%",
              background: "#FFFFFF",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ArrowRight size={26} color="#080911" />
          </div>
        </div>
      </div>

      {/* 4. Subtitle Gold Zone (Bottom: 380 - 440 px / TikTok Safe Box Compliant) */}
      {currentSub && (
        <div
          style={{
            position: "absolute",
            bottom: 390,
            left: 80,
            right: 80,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div
            style={{
              background: "rgba(8, 9, 17, 0.88)",
              border: "1px solid rgba(255, 255, 255, 0.15)",
              borderRadius: 20,
              padding: "16px 28px",
              boxShadow: "0 12px 35px rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(16px)",
              textAlign: "center",
            }}
          >
            <span style={{ fontSize: 32, fontWeight: 800, color: textWhite, lineHeight: 1.3 }}>
              {currentSub.text.split(currentSub.highlight).map((part, i, arr) => (
                <React.Fragment key={i}>
                  {part}
                  {i < arr.length - 1 && (
                    <span
                      style={{
                        color: cyanAccent,
                        borderBottom: `2px solid ${cyanAccent}`,
                        paddingBottom: 2,
                      }}
                    >
                      {currentSub.highlight}
                    </span>
                  )}
                </React.Fragment>
              ))}
            </span>
          </div>
        </div>
      )}

      {/* 5. Master Voiceover Track Sync */}
      <Audio
        src={staticFile("kyustore_web_promo_enceladus.mp3")}
        volume={1.0}
      />
    </AbsoluteFill>
  );
};
